import { toTeamsAppURL } from '@atlaskit/teams-app-config/utils/teams-app-navigation/to-teams-app-url';
import { type RequireOrgIdOrCloudId } from '@atlaskit/teams-app-config/types';

import { logException } from '../../../../common/utils/sentry';
import { getRelativeUrl, isAbsoluteLink } from '../../../../common/utils/url';

export const getSpaAndRelativePath = (
	href: string,
	contextSpaPaths: string[],
): {
	isSPA: boolean;
	relativePath: string;
} => {
	const relativePath = getRelativeUrl(href);
	const isSPA = isSPALink(relativePath, contextSpaPaths);

	return {
		isSPA,
		relativePath,
	};
};

/**
 * This function will check a link is SPA link or not basing on context path of product (Confluence or Jira).
 * If a given url is an absolute link, we check it by using host name and product context path.
 * If a given url is a relative link, there are 2 possible cases:
 * - `/<product-context-path>/link/path`: is a SPA link
 * - `link/path`: is a SPA link
 * - `/link/path`: is not a SPA link
 */
export const isSPALink = (url: string, contextSpaPaths: string[]): boolean => {
	if (!contextSpaPaths || !contextSpaPaths.length) {
		return false;
	}

	let isStartWithContextSPA: boolean;

	if (isAbsoluteLink(url)) {
		try {
			const { pathname, search, hash, origin } = new URL(url);
			const relativeUrl = `${pathname}${search}${hash}`;
			const isSameDomain = window.location.href.includes(origin);
			isStartWithContextSPA = !!contextSpaPaths.find((contextPath) => {
				return relativeUrl.startsWith(contextPath);
			});
			return isStartWithContextSPA && isSameDomain;
		} catch (error) {
			logException(error, 'Cannot parse URL in isSPALink', { url });
			return false;
		}
	}

	// is relative link
	isStartWithContextSPA = !!contextSpaPaths.find((contextPath) => {
		return url.startsWith(contextPath);
	});
	return isStartWithContextSPA || !url.startsWith('/');
};

export const isModified = (event: React.MouseEvent<HTMLAnchorElement>): boolean =>
	Boolean(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

/**
 * Converts an href to an absolute URL for Teams app navigation.
 *
 * @param href - The href to convert (absolute or relative)
 * @param config - Configuration object containing orgId or cloudId
 * @returns An absolute URL - returns the original href if already absolute, or converts relative href to Teams app URLs
 */
export const getAbsoluteLink = (href: string, config: RequireOrgIdOrCloudId): string =>
	isAbsoluteLink(href) ? href : toTeamsAppURL(href, '', config);
